import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import DataTable, { StatusPill, fmtDate } from "../components/DataTable";
import { apiCall } from "../auth/AuthContext";
import { Check, RefreshCw, Loader2, ExternalLink } from "lucide-react";

export default function AdminDocuments() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    try { const d = await apiCall("/api/admin/documents"); setRecords(d.records || []); }
    catch { setRecords([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const review = async (recordId, action) => {
    if (busyId) return;
    setBusyId(`${recordId}-${action}`);
    setToast(null);
    try {
      await apiCall(`/api/admin/documents/${recordId}`, { method: "PATCH", body: JSON.stringify({ action }) });
      setToast({ tone: "success", text: action === "approve" ? "Approved." : "Marked for resubmit." });
      await load();
    } catch (err) {
      setToast({ tone: "danger", text: err.message || "Update failed." });
    } finally {
      setBusyId(null);
      setTimeout(() => setToast(null), 3500);
    }
  };

  const TONE = (v) => {
    const s = (v || "").toLowerCase();
    if (s === "approved") return "success";
    if (s === "resubmit") return "danger";
    if (s === "pending review") return "warning";
    return "info";
  };

  const columns = [
    { key: "Name", label: "Client", sortable: true },
    { key: "Document Name", label: "Document" },
    { key: "Document Category", label: "Category" },
    { key: "Service Related To", label: "Service" },
    { key: "Upload Date", label: "Uploaded", fmt: fmtDate, sortable: true },
    { key: "Uploaded By", label: "By" },
    {
      key: "File Attachment", label: "File",
      fmt: (v) => Array.isArray(v) && v[0]?.url ? (
        <a href={v[0].url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-jc-gold hover:text-jc-gold-light">
          View <ExternalLink size={11} />
        </a>
      ) : <span className="text-jc-muted">—</span>
    },
    { key: "Review Status", label: "Status", fmt: (v) => <StatusPill value={v} tone={TONE(v)} /> },
    {
      key: "id", label: "Actions",
      fmt: (_, r) => {
        const isApproving = busyId === `${r.id}-approve`;
        const isResubmitting = busyId === `${r.id}-resubmit`;
        return (
          <div className="flex gap-2">
            <button
              data-testid={`doc-approve-${r.id}`}
              onClick={() => review(r.id, "approve")}
              disabled={!!busyId}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-mono bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isApproving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Approve
            </button>
            <button
              data-testid={`doc-resubmit-${r.id}`}
              onClick={() => review(r.id, "resubmit")}
              disabled={!!busyId}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-mono bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isResubmitting ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Resubmit
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminLayout title="DOCUMENT QUEUE" kicker="Review Pending">
      {toast && (
        <div data-testid="doc-toast" className={`mb-5 px-4 py-3 border font-mono text-[11px] uppercase tracking-[0.22em] ${toast.tone === "success" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-red-500/10 text-red-300 border-red-500/30"}`}>
          ◆ {toast.text}
        </div>
      )}
      <DataTable
        testId="admin-documents-table"
        loading={loading}
        records={records}
        columns={columns}
        searchKeys={["Name", "Document Name", "Document Category", "Review Status"]}
        emptyText="No documents uploaded yet."
      />
    </AdminLayout>
  );
}
